import { memo, useCallback, type ComponentProps } from 'react';

import { sendHotJarEvent } from 'src/hooks/useHotjarEvent';
import { useSaveJobHomeRecs } from 'src/hooks/useSaveJob/useSaveJobHomeRecs';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useCandidateDetails } from 'src/modules/hooks/useCandidateDetailsQuery';
import useSolMetadataReference from 'src/modules/hooks/useSolMetadataReference';
import SolImpressionTracker from 'src/modules/solTracker/SolImpressionTracker';
import { useCareerFeedJobAiSummariesFeatureFlag } from 'src/store/featureFlags/hooks';

import { useCareerFeedAIAssistant } from '../CareerFeedAIAssistant/hooks/useCareerFeedAIAssistant';
import JobCard from '../SharedComponent/JobCard/JobCard';
import { getRecommendationBadges } from '../SharedComponent/JobCard/hooks/getRecommendationBadges';

import { useHiddenJobsHomeRecs } from './useHiddenJobsHomeRecs';

export const RECOMMENDED_JOB_VIEW_ORIGIN = 'recom-homepage';
export const RECOMMENDED_JOB_AI_VIEW_ORIGIN = 'recom-homepage-genai';

const convertToSavedJob = (
  jobInfo: ComponentProps<typeof JobCard>['jobInfo'],
) => ({
  id: jobInfo.id,
  title: jobInfo.title,
  advertiser: {
    name: jobInfo.advertiserName,
  },
  salary: jobInfo.salaryLabel
    ? {
        currencyLabel: jobInfo.salaryCurrencyLabel ?? null,
        label: jobInfo.salaryLabel,
      }
    : null,
  location: {
    label: jobInfo.locationLabel,
  },
  listedAt: jobInfo.listingDateLabel
    ? {
        label: jobInfo.listingDateLabel,
      }
    : null,
  products: jobInfo.srcLogo
    ? {
        branding: {
          logo: {
            url: jobInfo.srcLogo,
          },
        },
      }
    : null,
});

const useCareerFeedJobAiSummaries = (
  bullets?: string[],
  enrichmentSummary?: string[],
) => {
  const careerFeedJobAiSummariesFeatureFlag =
    useCareerFeedJobAiSummariesFeatureFlag();
  switch (careerFeedJobAiSummariesFeatureFlag) {
    case 'replace':
      return bullets && bullets?.length > 0 ? enrichmentSummary : [];
    case 'all':
      return enrichmentSummary;
    default:
      return bullets;
  }
};

const JobCardWithTracking = (
  props: Omit<ComponentProps<typeof JobCard>, 'analytics'> & {
    position: number;
    isFeaturedOrDisplayKeyType: 'loggedInHomeRecs' | 'feedJobs';
  },
) => {
  const { id: seekerId } = useCandidateDetails();

  const { persistedCandidateChatContext } = useCareerFeedAIAssistant();

  const solMetadataReference = useSolMetadataReference({
    jobId: props.jobInfo.id,
    isFeaturedOrDisplayKeyType: props.isFeaturedOrDisplayKeyType,
  });

  const { toggleSavedJob: onSaveJobButtonClick } = useSaveJobHomeRecs({
    isSaved: props.jobInfo.isSaved,
    job: convertToSavedJob(props.jobInfo),
    solMetadataReference,
    analytics: {
      jobViewOriginQuery: persistedCandidateChatContext
        ? RECOMMENDED_JOB_AI_VIEW_ORIGIN
        : RECOMMENDED_JOB_VIEW_ORIGIN,
      jobListingPosition: props.position,
      jobViewType: 'job card',
      linkPosition: `${props.position}`,
    },
  });

  const { isJobHidden, hideJob, unhideJob } = useHiddenJobsHomeRecs(
    props.jobInfo.id,
  );

  const analyticsFacade = useAnalyticsFacade();
  const onHideJobButtonClick = useCallback(() => {
    hideJob();
    sendHotJarEvent('career_feed_hide_job_feedback', {
      jobId: props.jobInfo.id,
      candidateId: seekerId,
      // This is not great because we shouldn't reach out to solMetadata properties
      // But it's needed to ensure we can stich it back to Hubble event.
      token: props.jobInfo.solMetadata?.token,
    });

    analyticsFacade.jobAdSnippetHidePressed({
      jobId: props.jobInfo.id,
      solMetadata: props.jobInfo.solMetadata,
    });
  }, [
    hideJob,
    seekerId,
    analyticsFacade,
    props.jobInfo.id,
    props.jobInfo.solMetadata,
  ]);
  const onUnhideJobButtonClick = useCallback(() => {
    unhideJob();
    analyticsFacade.jobAdSnippetUnhidePressed({
      jobId: props.jobInfo.id,
      solMetadata: props.jobInfo.solMetadata,
    });
  }, [analyticsFacade, props.jobInfo, unhideJob]);

  const { analyticsBadges } = getRecommendationBadges(props.jobInfo.tags);

  return (
    <SolImpressionTracker
      jobId={props.jobInfo.id}
      solMetadata={props.jobInfo.solMetadata}
      jobTags={analyticsBadges}
    >
      <JobCard
        {...props}
        jobInfo={{
          ...props.jobInfo,
          bullets: useCareerFeedJobAiSummaries(
            props.jobInfo.bullets,
            props.jobInfo.enrichmentSummary,
          ),
        }}
        analytics={{
          linkName: 'dashboard-recommended-job-click',
          linkSection: 'Recommended Jobs',
          viewOrigin: persistedCandidateChatContext
            ? RECOMMENDED_JOB_AI_VIEW_ORIGIN
            : RECOMMENDED_JOB_VIEW_ORIGIN,
          position: props.position,
          solHash: solMetadataReference,
        }}
        onSaveJobButtonClick={onSaveJobButtonClick}
        onUnhideJobButtonClick={onUnhideJobButtonClick}
        onHideJobButtonClick={onHideJobButtonClick}
        isHidden={isJobHidden}
      />
    </SolImpressionTracker>
  );
};

export const RecommendedJobCard = memo(JobCardWithTracking);
